import {RWSService} from '@rws-framework/client';
import { capitalize } from "../helpers/utils";
import type { ModeValue } from "../types/mode";
import type { Course } from "../types/course";

const SPECIAL_ROUTE_TAILS = [
  'edit', 'dash', 'grades', 'reports', 'dashboard', 'instructor_notes', 'liveview',
  'instructor_resources', 'course_setup', 'activity_settings_new', 'activity_settings',
  'enrollments', 'late_work_manager', 'performance_report', 'report_student_deepdive',
  'assessment-deep-dive', 'lti_sync_manager', 'grading_center', 'gradebook',
];

type ContentContext = 'course' | 'lesson' | 'page';

type SavedContentIds = {
  lesson_id?: string;
  page_id?: string;
};

/** Route params (a copy of ui-router's `$stateParams`) the caller was rendered with. */
type ContentContextIds = {
  lessonId?: string;
  pageId?: string;
};

type OwnedContent = {
  owned_by?: string;
};

class ContentsService extends RWSService {
  static _IN_CLIENT: boolean = true;

  setCourseMode(courseId: string, mode: ModeValue): void {
    sessionStorage.setItem('mode', courseId + mode);
  }

  getCourseMode(course_id: string): ModeValue {
    if (!course_id) {
      sessionStorage.setItem('mode', '');
      return 'presentation';
    }
    const mode = sessionStorage.getItem('mode');
    if (mode?.includes(course_id)) {
      return mode.replace(course_id, '') as ModeValue;
    }
    sessionStorage.setItem('mode', course_id + 'presentation');
    return 'presentation';
  }

  getView(course: Course, user_id: string, editModeValue: ModeValue): boolean {
    const hasCourseContentRights = sessionStorage.getItem('course_content_rights') === 'yes';

    const adminCourseIds = (sessionStorage.getItem('admin_course_ids') || '').split(',').filter(Boolean);
    const inAdminScope = adminCourseIds.includes(course._id);

    const coordinatorCourseIds = (sessionStorage.getItem('coordinator_course_ids') || '').split(',').filter(Boolean);
    const inCoordinatorScope = coordinatorCourseIds.includes(course._id);

    const isProfessor = (course.instructors||[]).concat(course.assistants||[]).includes(user_id)
      || hasCourseContentRights || inAdminScope || inCoordinatorScope;
    return !isProfessor || editModeValue == 'student';
  }

  setCourseTemplate(courseId: string): void {
    const currentUrl = this.getLocation();

    if (!currentUrl) return;

    const locationTail = currentUrl.split("/").slice(-1)[0];
    const newMode = this.getCourseMode(courseId);

    if (newMode === 'edit' && !SPECIAL_ROUTE_TAILS.includes(locationTail)) {
      let newUrl = currentUrl + '/edit';

      if (window.stateParams?.componentId) {
        newUrl = newUrl.replace(window.stateParams.componentId, '');
      }
      this.navigateTo(newUrl.replace('//', '/'));
      return;
    }

    if (newMode === 'presentation' && locationTail === 'edit') {
      if (window.stateParams?.componentId) {
          this.navigateTo(currentUrl.slice(0,-30));
      } else {
          this.navigateTo(currentUrl.slice(0,-5));
      }    
    }
  }


  getLocation(): string {
    let url = '';
    document.documentElement.dispatchEvent(
      new CustomEvent('jnct:router:getlink', {
        detail: (currentUrl: string) => { url = currentUrl; },
      })
    );
    return url || window.location.hash.slice(1);
  }

  isOwnContent(obj: OwnedContent, user_id: string): boolean {
    return obj.owned_by === user_id;
  }

  navigateToSavedContent(context: ContentContext, promise: Promise<{ data: SavedContentIds }>): void {
    promise.then((response) => {
      if (!response?.data) return;

      document.documentElement.dispatchEvent(
        new CustomEvent('jnct:notifications:delayed', {
          detail: {
            message: capitalize(context) + ' updated',
            type: 'success',
            time: 1000,
          },
        })
      );

      if (context === 'course') {
          this.navigateTo(window.stateParams.courseId + '/edit');
      } else if (context === 'lesson') {
          this.navigateTo(window.stateParams.courseId + '/' + response.data.lesson_id + '/edit');
      } else  if (context === 'page') {
          this.navigateTo(window.stateParams.courseId + '/' + response.data.lesson_id + '/' + response.data.page_id + '/edit');
      }
    });
  }

  navigateTo(url: string): void {
    document.documentElement.dispatchEvent(
      new CustomEvent('jnct:router:gotolink', { detail: url })
    );
  }

  navigateIfIdsChanged(context: ContentContextIds, data: SavedContentIds): void {
    if (data.page_id && context.pageId && data.page_id !== context.pageId) {
        this.navigateTo(window.stateParams.courseId + '/' + data.lesson_id + '/' + data.page_id + '/edit');
    } else if (data.lesson_id && context.lessonId && data.lesson_id !== context.lessonId) {
        this.navigateTo(window.stateParams.courseId + '/' + data.lesson_id + '/edit');
    }
  }
}

export default ContentsService.getSingleton()

export {ContentsService as ContentsServiceInstance}
